import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Alert,
  IconButton,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload, Download, Delete,
} from '@mui/icons-material';
import {
  getDocuments, uploadDocument, downloadDocument, deleteDocument,
} from '../services/authService';

function DocumentsForm() {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDocuments();
      setDocuments(data.data || []);
    } catch (err) {
      setError(t('pages.profile.documents.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const MAX_FILE_SIZE_MB = 10;
  const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.txt', '.csv', '.rtf', '.odt', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const ext = `.${file.name.split('.').pop().toLowerCase()}`;
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(t('pages.profile.documents.invalidFileType'));
      const fileInput = document.getElementById('document-file-input');
      if (fileInput) fileInput.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(t('pages.profile.documents.fileTooLarge', { maxSize: MAX_FILE_SIZE_MB }));
      const fileInput = document.getElementById('document-file-input');
      if (fileInput) fileInput.value = '';
      return;
    }

    setError(null);
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          await uploadDocument({
            file: e.target.result,
            originalName: selectedFile.name,
            description,
          });
          setSelectedFile(null);
          setDescription('');
          setSuccess(t('pages.profile.documents.uploadSuccess'));
          // Reset file input
          const fileInput = document.getElementById('document-file-input');
          if (fileInput) fileInput.value = '';
          await loadDocuments();
        } catch (err) {
          setError(err.response?.data?.error || t('pages.profile.documents.uploadError'));
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(selectedFile);
    } catch (err) {
      setError(t('pages.profile.documents.uploadError'));
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await downloadDocument(doc.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.originalName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(t('pages.profile.documents.downloadError'));
    }
  };

  const handleDelete = async (doc) => {
    setDeleteTarget(doc);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteDialogOpen(false);
    if (!deleteTarget) return;

    try {
      await deleteDocument(deleteTarget.id);
      setSuccess(t('pages.profile.documents.deleteSuccess'));
      await loadDocuments();
    } catch (err) {
      setError(err.response?.data?.error || t('pages.profile.documents.deleteError'));
    }
    setDeleteTarget(null);
  };

  return (
    <div className="profile-form">
      <Typography variant="h6" gutterBottom>
        {t('pages.profile.documents.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('pages.profile.documents.upload')}
        </Typography>
        <Box sx={{
          display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap',
        }}
        >
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUpload />}
          >
            {selectedFile ? selectedFile.name : t('pages.profile.documents.selectFile')}
            <input
              id="document-file-input"
              type="file"
              hidden
              accept=".pdf,.jpg,.jpeg,.png,.webp,.txt,.csv,.rtf,.odt,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              onChange={handleFileSelect}
            />
          </Button>
          <TextField
            label={t('pages.profile.documents.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
          >
            {t('pages.profile.documents.uploadButton')}
          </Button>
        </Box>
      </Paper>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('pages.profile.documents.fileName')}</TableCell>
                <TableCell>{t('pages.profile.documents.description')}</TableCell>
                <TableCell>{t('pages.profile.documents.uploadedBy')}</TableCell>
                <TableCell>{t('pages.profile.documents.date')}</TableCell>
                <TableCell align="right">{t('pages.profile.documents.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {t('pages.profile.documents.noDocuments')}
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>{doc.originalName}</TableCell>
                    <TableCell>{doc.description || '-'}</TableCell>
                    <TableCell>
                      {doc.uploadedByAdmin
                        ? t('pages.profile.documents.admin')
                        : t('pages.profile.documents.user')}
                    </TableCell>
                    <TableCell>{doc.createdAt}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleDownload(doc)}
                        title={t('pages.profile.documents.download')}
                      >
                        <Download />
                      </IconButton>
                      {!doc.uploadedByAdmin && (
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(doc)}
                          title={t('pages.profile.documents.delete')}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('pages.profile.documents.delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('pages.profile.documents.deleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.button.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('pages.profile.documents.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DocumentsForm;
